import SEO from "../components/seo.jsx"
import { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
const cinzel = { fontFamily: "'Cinzel', serif" };

const FAQS = [
  {
    q: "How much will it cost?",
    a: "The cost of a hair transplant surgery depends on several factors like the technique used, the number of sessions required and the number of grafts to be implanted. We'd recommend forwarding your pictures to our clinic's official number, and one of our representatives will revert with the surgery quotation.",
  },
  {
    q: "Is the hair transplant a permanent solution?",
    a: "Hair loss is a dynamic process that continues with the age of the patient. Transplanted hairs have an expectancy of 15–20 years. But if a patient undergoes transplant at a very young age, there's a possibility they might require a corrective session 10–12 years after surgery.",
  },
  {
    q: "Are the transplanted hairs permanent?",
    a: "The procedure is based on the theory of donor dominance. The life of transplanted hairs depends on the technique used, the patient's lifestyle habits, and the quality of donor hairs. On average, transplanted hairs have an expectancy of 15–20 years, after which density might reduce to a certain extent.",
  },
  {
    q: "How much time will the surgery take?",
    a: "On average our team requires approximately 6 hours to complete a session of transplanting 2500 grafts. A tea break and post-surgery lunch are routinely planned to make the patient's experience as comfortable as possible.",
  },
  {
    q: "How long should I wait between hair transplant procedures?",
    a: "If a second surgery is required in the same region, we prefer to operate 6–8 months after the first, once hair length is adequate to assess styling and plan distribution for future sessions. If the transplant is in a different area, it can potentially be done 2–3 months after surgery.",
  },
  {
    q: "Is the procedure very painful?",
    a: "Our FUE technique is designed so patients have minimal to no discomfort during or after the procedure. We routinely use topical anaesthetics (cream/gels) and skin vibrators to keep the procedure as comfortable as possible.",
  },
  {
    q: "When will the newly transplanted hairs start growing?",
    a: "Transplanted hairs start growing after 3–4 months of the procedure, at an average speed of 1 cm per month depending on individual response. It takes approximately 6–8 months to evaluate the actual results of a hair transplant.",
  },
  {
    q: "How many days do I need to rest after surgery? Or how much is the recovery time?",
    a: "Little to no time off from your routine schedule or work is required — some patients return to work the very same day, wearing a provided cap. If you'd rather ease back in, many clients schedule the procedure just before a weekend for 2–3 days of rest at home. Strenuous exercise and swimming should be avoided for 2 weeks after surgery.",
  },
  {
    q: "Does the surgery have anything to do with the brain?",
    a: "Hair transplant is surgery of the scalp (skin) only, with no involvement of any deep tissues. It's a very safe surgery, but one that requires years of dedicated learning and practice to achieve the desired results.",
  },
  {
    q: "How many grafts will I require?",
    a: "The number of grafts required depends on the grade of baldness, which may range from grade 1 to grade 7. Contact us at our centre or schedule a virtual consultation online, so we can give you an idea of the number of grafts required for your particular stage.",
  },
];

/* ============================================================
   SUB-COMPONENTS
============================================================ */
function Eyebrow({ children }) {
  return (
    <p
      style={cinzel}
      className="text-[10.5px] tracking-[0.28em] uppercase mb-3 flex items-center justify-center gap-2.5 text-[#a07828]"
    >
      <span className="w-6 h-px bg-[#c9a84c]" />
      {children}
      <span className="w-6 h-px bg-[#c9a84c]" />
    </p>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-colors duration-300 relative ${
        open ? "border-[#c9a84c]/40 bg-[#c9a84c]/[0.04]" : "border-[#c9a84c]/18 bg-white"
      }`}
    >
      <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
      <button
        className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5 sm:py-6 text-left"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
      >
        <span className="flex items-start sm:items-center gap-4">
          <span
            style={cinzel}
            className="hidden sm:flex w-8 h-8 flex-shrink-0 rounded-full items-center justify-center text-[11px] font-black text-[#c9a84c] border border-[#c9a84c]/30"
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <span style={cinzel} className="text-[13px] sm:text-[13.5px] font-bold tracking-[0.03em] text-[#1a0f02] leading-snug">
            {q}
          </span>
        </span>
        <span
          className={`flex-shrink-0 w-8 h-8 rounded-full border border-[#c9a84c]/35 flex items-center justify-center transition-transform duration-300 ${
            open ? "rotate-45 bg-[#c9a84c] border-[#c9a84c]" : ""
          }`}
        >
          <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke={open ? "#1a0f02" : "#c9a84c"} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="px-5 sm:px-7 pb-6 sm:pl-[68px]">
            <p className="text-[13px] sm:text-[13.5px] leading-[1.85] text-[#6b5535]">{a}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PAGE COMPONENT
============================================================ */
export default function FaqPage({ url = "https://www.hairclinique.in/faq.php" }) {
  const pageRef = useRef(null);
  const heroHeadRef = useRef(null);
  const heroSubRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.12 });

      gsap.fromTo(
        sectionRef.current,
        { opacity: 0, y: 36 },
        {
          opacity: 1,
          y: 0,
          duration: 0.75,
          ease,
          scrollTrigger: { trigger: sectionRef.current, start: "top 88%", toggleActions: "play none none reverse" },
        }
      );
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const wrap = "max-w-3xl mx-auto px-5 sm:px-8";

  return (
    <>
<SEO
  title="Hair Transplant FAQs | Dr. Bhavesh Gupta - Clinique Internationale"
  description="Get answers to common questions about Sapphire FUE hair transplant, recovery, cost, and results at Clinique Internationale with Dr. Bhavesh Gupta."
  url={url}
/>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap"
        rel="stylesheet"
      />

      <div
        ref={pageRef}
        className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20"
        style={{ fontFamily: "'Raleway', sans-serif" }}
      >
        {/* ══════════════════════════════════════
            HERO
        ══════════════════════════════════════ */}
        <div className="relative h-[220px] sm:h-[280px] md:h-[340px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0e0905] to-[#1a0f02]" />
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")",
            }}
          />
          <div className="relative z-10 text-center px-5">
            <p style={cinzel} className="text-[10.5px] tracking-[0.32em] uppercase text-[#c9a84c] mb-4 flex items-center justify-center gap-3">
              <span className="w-7 h-px bg-[#c9a84c]" />
              Have Questions?
              <span className="w-7 h-px bg-[#c9a84c]" />
            </p>
            <h1
              ref={heroHeadRef}
              style={cinzel}
              className="font-black text-[1.8rem] sm:text-[2.4rem] md:text-[3rem] tracking-[0.04em] uppercase text-white mb-3 leading-tight"
            >
              Frequently Asked Questions
            </h1>
            <p ref={heroSubRef} className="text-[14px] sm:text-[15px] text-white/70 max-w-lg mx-auto leading-relaxed">
              Everything you need to know about cost, recovery time, and the hair transplant process.
            </p>
          </div>
        </div>

        {/* ── BREADCRUMB ── */}
        <div className="bg-[#0e0905]/95 border-b border-[#c9a84c]/10">
          <div className={`${wrap} py-3 flex flex-wrap items-center gap-2`}>
            <a href="/" style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">
              Home
            </a>
            <span className="text-[#c9a84c]/30 text-xs">›</span>
            <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">
              FAQ
            </span>
          </div>
        </div>

        {/* ══════════════════════════════════════
            FAQ ACCORDION
        ══════════════════════════════════════ */}
        <div className={`${wrap} py-16 sm:py-20 md:py-24`}>
          <div ref={sectionRef}>
            <Eyebrow>Common Questions</Eyebrow>
            <h2
              style={cinzel}
              className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.85rem] text-center uppercase tracking-[0.05em] mb-12 text-[#1a0f02]"
            >
              Everything About Your Hair Transplant Journey
            </h2>

            <div className="flex flex-col gap-4">
              {FAQS.map((f, i) => (
                <FaqItem key={i} index={i} q={f.q} a={f.a} />
              ))}
            </div>

            {/* still have a question CTA */}
            <div className="mt-12 relative overflow-hidden bg-[#0e0905] border border-[#c9a84c]/20 rounded-2xl p-8 sm:p-10 text-center">
              <span className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/50 to-transparent" />
              <p style={cinzel} className="text-[13px] sm:text-[14px] font-bold tracking-[0.05em] uppercase text-white mb-2">
                Still Have a Question?
              </p>
              <p className="text-[13px] text-white/55 mb-6 max-w-md mx-auto leading-relaxed">
                Send us your pictures or book a consultation — our team will get back to you with personalised guidance.
              </p>
              <a
                href="https://api.whatsapp.com/send?phone=919082123009&text=I%27m%20interested%20in%20Hair%20Restoration"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...cinzel,
                  backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)",
                  backgroundSize: "200% 100%",
                }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                Chat With Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}